import { ref, set, get, update } from 'firebase/database';
import { db } from '../config/firebase';
import { CreateUserData, User } from '../types';

/**
 * Service for managing user profile and wallet documents in Firebase RTDB.
 * Handles user creation, updates, and Stripe customer ID association.
 */
class AuthService {
  // New structure: user_data/{uid}/profile and user_data/{uid}/wallet
  private getProfilePath(uid: string, testMode: boolean = false): string {
    const basePath = `user_data/${uid}/profile`;
    return testMode ? `testMode/${basePath}` : basePath;
  }

  private getWalletPath(uid: string, testMode: boolean = false): string {
    const basePath = `user_data/${uid}/wallet`;
    return testMode ? `testMode/${basePath}` : basePath;
  }

  async createUserDocument(userData: CreateUserData, testMode: boolean = false): Promise<void> {
    const profileRef = ref(db, this.getProfilePath(userData.uid, testMode));
    const walletRef = ref(db, this.getWalletPath(userData.uid, testMode));

    // Check if user already exists
    const profileSnap = await get(profileRef);
    if (profileSnap.exists()) {
      // Update last login
      await this.updateLastLogin(userData.uid, testMode);
      return;
    }

    // Create user profile document
    const now = Date.now();
    await set(profileRef, {
      email: userData.email,
      displayName: userData.displayName || null,
      photoURL: userData.photoURL || null,
      stripeCustomerId: null,
      createdAt: now,
      updatedAt: now,
      lastLoginAt: now,
      emailVerified: false,
      provider: userData.provider,
      ...(userData.referredBy ? { referredBy: userData.referredBy } : {}),
    });

    // Create wallet document
    await set(walletRef, {
      balance: 0,
      lifetimeTokensPurchased: 0,
      lifetimeTokensSpent: 0,
      lifetimeTokensGranted: 0,
      createdAt: now,
      updatedAt: now,
    });
  }

  async createOrUpdateUserDocument(
    userData: CreateUserData & { photoURL?: string | null },
    testMode: boolean = false
  ): Promise<void> {
    const profileRef = ref(db, this.getProfilePath(userData.uid, testMode));
    const walletRef = ref(db, this.getWalletPath(userData.uid, testMode));

    const profileSnap = await get(profileRef);
    const now = Date.now();

    if (profileSnap.exists()) {
      // Update existing user
      const existingData = profileSnap.val();
      await update(profileRef, {
        displayName: userData.displayName || existingData.displayName,
        photoURL: userData.photoURL || existingData.photoURL,
        lastLoginAt: now,
        updatedAt: now,
      });
    } else {
      // Create new user profile
      await set(profileRef, {
        email: userData.email,
        displayName: userData.displayName || null,
        photoURL: userData.photoURL || null,
        stripeCustomerId: null,
        createdAt: now,
        updatedAt: now,
        lastLoginAt: now,
        emailVerified: true,
        provider: userData.provider,
        ...(userData.referredBy ? { referredBy: userData.referredBy } : {}),
      });

      // Create wallet for new user
      await set(walletRef, {
        balance: 0,
        lifetimeTokensPurchased: 0,
        lifetimeTokensSpent: 0,
        lifetimeTokensGranted: 0,
        createdAt: now,
        updatedAt: now,
      });
    }
  }

  async updateLastLogin(uid: string, testMode: boolean = false): Promise<void> {
    const profileRef = ref(db, this.getProfilePath(uid, testMode));
    await update(profileRef, {
      lastLoginAt: Date.now(),
    });
  }

  async getUser(uid: string, testMode: boolean = false): Promise<User | null> {
    const profileRef = ref(db, this.getProfilePath(uid, testMode));
    const profileSnap = await get(profileRef);

    if (!profileSnap.exists()) {
      return null;
    }

    // Return user data with uid included
    return {
      uid,
      ...profileSnap.val(),
    } as User;
  }

  async updateStripeCustomerId(uid: string, stripeCustomerId: string, testMode: boolean = false): Promise<void> {
    const profileRef = ref(db, this.getProfilePath(uid, testMode));
    await update(profileRef, {
      stripeCustomerId,
      updatedAt: Date.now(),
    });
  }
}

export const authService = new AuthService();
export default authService;
